import type { AdminBookingResponseDto } from '@pro-sto/api-client';
import { getBookingPostUuid } from './getBookingPostUuid';

type BookingLike = Pick<
  AdminBookingResponseDto,
  'uuid' | 'status' | 'start_time' | 'end_time' | 'client_uuid' | 'post_uuid' | 'created_by_source' | 'car_uuid'
> & {
  client?: { uuid?: string } | null;
  post?: { uuid?: string } | null;
  car?: {
    uuid?: string;
    license_plate?: string | { number?: unknown; region?: unknown } | null;
  } | null;
};

export interface ClientOverlapParams {
  clientUuid: string;
  carUuid?: string;
  /**
   * Госномер входящей записи. Используется, если carUuid ещё нет
   * (новая машина в create) или uuid существующей записи недоступен.
   */
  carLicensePlate?: string;
  start: Date;
  end: Date;
  postUuid?: string;
  excludeBookingUuid?: string;
}

export type ClientOverlapKind = 'app_block' | 'admin_confirm';

export interface ClientOverlapResult {
  kind: ClientOverlapKind;
  booking: BookingLike;
}

type CarMatch = 'same' | 'different' | 'unknown';

const getClientUuid = (booking: BookingLike): string | undefined =>
  booking.client?.uuid ?? booking.client_uuid;

const getCarUuid = (booking: BookingLike): string | undefined => {
  if (booking.car?.uuid) return booking.car.uuid;
  return typeof booking.car_uuid === 'string' ? booking.car_uuid : undefined;
};

const normalizeLicensePlate = (value: string | undefined): string =>
  (value ?? '').replace(/[\s-]/g, '').toUpperCase();

const extractLicensePlate = (plate: unknown): string | undefined => {
  if (typeof plate === 'string' && plate.trim()) return plate;
  if (plate && typeof plate === 'object') {
    const record = plate as { number?: unknown; region?: unknown };
    const number = typeof record.number === 'string' ? record.number.trim() : '';
    const region = typeof record.region === 'string' ? record.region.trim() : '';
    const combined = region ? `${number} ${region}` : number;
    return combined || undefined;
  }
  return undefined;
};

const getCarLicensePlate = (booking: BookingLike): string | undefined =>
  extractLicensePlate(booking.car?.license_plate);

/**
 * Сравнивает машину входящей записи с уже существующей.
 * Если нет ни uuid, ни госномера хотя бы с одной стороны — результат unknown:
 * app_block по «той же машине» недоказуем, молча пропускать нельзя.
 */
const compareCars = (booking: BookingLike, params: ClientOverlapParams): CarMatch => {
  const existingUuid = getCarUuid(booking);
  const incomingUuid = params.carUuid;

  if (incomingUuid && existingUuid) {
    return incomingUuid === existingUuid ? 'same' : 'different';
  }

  const existingPlate = normalizeLicensePlate(getCarLicensePlate(booking));
  const incomingPlate = normalizeLicensePlate(params.carLicensePlate);

  if (incomingPlate && existingPlate) {
    return incomingPlate === existingPlate ? 'same' : 'different';
  }

  return 'unknown';
};

const isTimeOverlap = (
  startMs: number,
  endMs: number,
  booking: BookingLike,
): boolean => {
  const bookingStart = new Date(booking.start_time).getTime();
  const bookingEnd = new Date(booking.end_time).getTime();
  return startMs < bookingEnd && bookingStart < endMs;
};

const findOverlappingOnOtherPost = (
  bookings: BookingLike[],
  params: ClientOverlapParams,
): BookingLike[] => {
  const startMs = params.start.getTime();
  const endMs = params.end.getTime();
  if (!params.clientUuid || endMs <= startMs) return [];

  return bookings.filter((booking) => {
    if (booking.uuid === params.excludeBookingUuid) return false;
    if (booking.status === 'cancelled') return false;
    if (getClientUuid(booking) !== params.clientUuid) return false;

    const postUuid = getBookingPostUuid(booking);
    if (params.postUuid && postUuid === params.postUuid) return false;

    return isTimeOverlap(startMs, endMs, booking);
  });
};

/**
 * Пересечение клиента на другом посту:
 * - client_app + та же машина (uuid или госномер) → блокировка;
 * - admin_panel на другом посту → подтверждение (только в модалке создания);
 * - client_app + другая машина или неопределённая машина → разрешено.
 */
export const detectClientOverlapOnOtherPost = (
  bookings: BookingLike[],
  params: ClientOverlapParams,
): ClientOverlapResult | undefined => {
  const overlapping = findOverlappingOnOtherPost(bookings, params);

  const appSameCar = overlapping.find((booking) => {
    if (booking.created_by_source !== 'client_app') return false;
    return compareCars(booking, params) === 'same';
  });
  if (appSameCar) {
    return { kind: 'app_block', booking: appSameCar };
  }

  const adminOverlap = overlapping.find((booking) => booking.created_by_source === 'admin_panel');
  if (adminOverlap) {
    return { kind: 'admin_confirm', booking: adminOverlap };
  }

  return undefined;
};

/** @deprecated Используйте detectClientOverlapOnOtherPost */
export const findClientOverlapOnOtherPost = (
  bookings: BookingLike[],
  params: ClientOverlapParams,
): BookingLike | undefined => detectClientOverlapOnOtherPost(bookings, params)?.booking;

export const getServicesDurationMinutes = (
  mainDuration: number | undefined,
  additionalDurations: Array<number | undefined>,
): number => {
  const main = mainDuration && mainDuration > 0 ? mainDuration : 60;
  const extra = additionalDurations.reduce<number>((sum, duration) => {
    return sum + (duration && duration > 0 ? duration : 0);
  }, 0);
  return main + extra;
};
